import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ShieldCheck, Search, CheckCircle2, Clock, LogOut } from 'lucide-react';

export function GuardCheckinTab({
  guardSearchQuery,
  setGuardSearchQuery,
  guardSearchResults,
  guardSearchLoading,
  handleGuardSearch,
  handleGuardCheckin,
  guardCheckinLoading,
  latestGuardCheckin,
  guardCheckins,
  handleGuardCheckout
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="shadow-lg border-0" data-testid="guard-checkin-search-card">
        <CardHeader className="bg-gradient-to-r from-gk-teal to-emerald-700 text-white rounded-t-xl">
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Guard Check-in
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2">
            <Input
              data-testid="guard-search-input"
              placeholder="Search guard by name or ID..."
              value={guardSearchQuery}
              onChange={(e) => setGuardSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleGuardSearch(); }}
            />
            <Button
              data-testid="guard-search-btn"
              onClick={handleGuardSearch}
              disabled={guardSearchLoading}
              className="bg-gk-teal hover:bg-emerald-700 shrink-0"
            >
              <Search className="w-4 h-4 mr-1" />
              {guardSearchLoading ? 'Searching...' : 'Search'}
            </Button>
          </div>

          {guardSearchResults.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Search Results</p>
              {guardSearchResults.map((g) => (
                <div key={g.id} data-testid={`guard-search-result-${g.id}`} className="flex items-center justify-between p-3 rounded-lg border bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gk-teal/10 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-gk-teal" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{g.full_name}</p>
                      <p className="text-xs text-slate-500">{g.id_type}: {g.id_number}</p>
                    </div>
                  </div>
                  <Button
                    data-testid={`guard-checkin-btn-${g.id}`}
                    size="sm"
                    onClick={() => handleGuardCheckin(g.id)}
                    disabled={guardCheckinLoading === g.id}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    {guardCheckinLoading === g.id ? 'Checking in...' : 'Check In'}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {guardSearchQuery && guardSearchResults.length === 0 && !guardSearchLoading && (
            <p className="text-sm text-center text-slate-400 py-4">No guards found matching "{guardSearchQuery}"</p>
          )}

          {latestGuardCheckin && (
            <div className="mt-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2">Latest Guard On Duty</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-emerald-900">{latestGuardCheckin.guard_name}</p>
                  <p className="text-xs text-emerald-700">{latestGuardCheckin.guard_id_number}</p>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    In: {new Date(latestGuardCheckin.checked_in_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                  </p>
                  {latestGuardCheckin.checked_out_at && (
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Out: {new Date(latestGuardCheckin.checked_out_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                    </p>
                  )}
                </div>
                {!latestGuardCheckin.checked_out_at && (
                  <Button
                    data-testid="guard-checkout-btn"
                    size="sm"
                    variant="outline"
                    onClick={() => handleGuardCheckout(latestGuardCheckin.id)}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Check Out
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-lg border-0" data-testid="guard-checkin-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="w-5 h-5 text-gk-navy-light" />
            Check-in History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {guardCheckins.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No check-in records yet</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {guardCheckins.map((c) => (
                <div key={c.id} data-testid={`guard-checkin-record-${c.id}`} className={`flex items-center justify-between p-3 rounded-lg border ${c.checked_out_at ? 'bg-slate-50' : 'bg-emerald-50 border-emerald-200'}`}>
                  <div>
                    <p className="font-semibold text-sm">{c.guard_name}</p>
                    <p className="text-xs text-slate-500">
                      In: {new Date(c.checked_in_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                    </p>
                    {c.checked_out_at && (
                      <p className="text-xs text-slate-500">
                        Out: {new Date(c.checked_out_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {c.checked_out_at ? (
                      <Badge variant="secondary" className="text-[10px]">Completed</Badge>
                    ) : (
                      <>
                        <Badge className="bg-emerald-600 text-[10px]">On Duty</Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleGuardCheckout(c.id)}
                          className="text-red-500 hover:text-red-700 h-7 text-xs"
                        >
                          <LogOut className="w-3 h-3 mr-1" /> Out
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
